import { useState } from 'react';
import { ticketsAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Stamp, Plus, Loader2, CheckCircle2, XCircle, Ban, Clock } from 'lucide-react';
import { toast } from 'sonner';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';
import SelectorDePersona from '@/components/SelectorDePersona';

/**
 * Las autorizaciones de un caso: quién tiene que dar el visto bueno, y qué contestó cada uno.
 *
 * **Es del lado de quien atiende el caso**, no de quien autoriza. Acá se piden y se ve cómo
 * vienen; el que autoriza las responde desde *Mis solicitudes* o desde *Mi trabajo*.
 *
 * **Un caso puede tener varias** —dos gerentes de áreas distintas, el director, el jefe del
 * área— y por eso esto es una lista con estado y no un sí/no. Con cuatro autorizaciones, saber
 * *cuál* falta es la mitad de la información.
 *
 * **Y no frena nada.** El panel lo dice arriba: qué hacer con un rechazo lo decide quien tiene
 * el caso. Si dejara creer que bloquea, alguien confiaría en un bloqueo que no existe.
 */
//: **Las etiquetas dicen de quién, no sólo qué.** Las tres primeras decían «el jefe de quien
//: pidió», «el gerente de su área» y «el jefe de esta área»: la diferencia entera vivía en un
//: pronombre. Diego eligió una el 2026-09-02 pensando en otra —acertó, pero por suerte— y el que
//: se equivoque en la dirección contraria se lleva una tarea sin autorizante, que es un fallo
//: silencioso: la autorización existe, no la espera nadie y nadie la va a responder.
//:
//: `pista` dice **de dónde sale** cada una, que es la otra mitad: dos salen de algo que la
//: cápsula tiene que haber declarado y tres se resuelven solas.
const COMO = [
  { id: 'jefe_del_solicitante', label: 'El jefe de quien pidió el caso',
    pista: 'Sale del organigrama, o del jefe cargado a mano en la ficha de la persona.' },
  { id: 'gerente_del_solicitante', label: 'El gerente del área de quien pidió',
    pista: 'Es el responsable cargado en esa gerencia, en Config Cápsula → Intents.' },
  { id: 'jefe_de_la_capsula', label: 'El jefe de esta área (la que atiende)',
    pista: 'Es quien ocupe el rol marcado como jefatura, en Config Cápsula → Intents.' },
  { id: 'rol', label: 'Un rol del equipo…' },
  // **Dos listas y no una.** «Del equipo» son los once de la cápsula; «del directorio» son las
  // 233 personas de la organización. La segunda es la que hace falta de verdad: quien autoriza
  // suele ser un gerente de otra área, que por definición **no está en el equipo**. Tenerlas
  // juntas obligaba a buscar a un gerente de Compras en una lista donde no puede estar.
  { id: 'persona', label: 'Una persona de este equipo…' },
  { id: 'persona_directorio', label: 'Cualquier persona de la organización…' },
];

//: Las dos últimas se guardan igual —`como: "persona"`, con el id de la persona— y sólo cambian
//: en **dónde se la busca**. El backend no tiene por qué distinguirlas: en las dos el resultado
//: es una persona concreta elegida de una lista. Un valor mas en el catalogo de resolvedores
//: para la misma resolucion seria vocabulario que despues hay que explicar.
const ELIGE_PERSONA = new Set(['persona', 'persona_directorio']);
const comoSeGuarda = (como) => (ELIGE_PERSONA.has(como) ? 'persona' : como);

const ESTADO = {
  aprobada: { icono: CheckCircle2, clase: 'text-green-700', texto: 'Aprobada' },
  rechazada: { icono: XCircle, clase: 'text-red-700', texto: 'Rechazada' },
  cancelada: { icono: Ban, clase: 'text-zinc-500', texto: 'Cancelada' },
};

export default function AutorizacionesDelCaso({
  tareas = [], tenantId, capsuleId, ticketId, teamRoles = [], personas = [], onCambio,
}) {
  const [abriendo, setAbriendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [como, setComo] = useState('jefe_del_solicitante');
  const [valor, setValor] = useState('');
  const [titulo, setTitulo] = useState('');

  const autorizaciones = tareas.filter((t) => t.task_type === 'autorizacion');
  const pendientes = autorizaciones.filter((t) => !t.autorizacion?.resultado);
  const necesitaValor = ['rol', 'persona', 'persona_directorio'].includes(como);

  const pedir = async () => {
    if (necesitaValor && !valor) { toast.error('Elegí a quién'); return; }
    setGuardando(true);
    try {
      await ticketsAPI.crearAutorizacion(tenantId, capsuleId, ticketId, {
        titulo: titulo.trim(), como: comoSeGuarda(como), valor: valor || null,
      });
      toast.success('Autorización pedida');
      setAbriendo(false); setTitulo(''); setValor('');
      onCambio?.();
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudo pedir la autorización'));
    } finally { setGuardando(false); }
  };

  return (
    <div className="border border-brand-border rounded-sm p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Stamp className="w-4 h-4 text-zinc-500" />
          <span className="text-sm font-medium">Autorizaciones</span>
          {!!autorizaciones.length && (
            <Badge variant="outline" className="text-[9px] rounded-sm">
              {autorizaciones.length - pendientes.length} de {autorizaciones.length} respondidas
            </Badge>
          )}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm"
                data-testid="pedir-autorizacion" onClick={() => setAbriendo((v) => !v)}>
          <Plus className="w-3 h-3 mr-1" /> Pedir
        </Button>
      </div>

      <QueSeConfiguraAca
        testId="ayuda-autorizaciones-del-caso"
        queEs="A quién le pedís el visto bueno para este caso, y qué contestó."
        paraQue="Hasta ahora esto pasaba por mail: alguien respondía «se autoriza» en un hilo y esa decisión no quedaba en ningún lado. Acá queda, con quién, cuándo y por qué."
        aQueAfecta={<>A <b>nadie automáticamente</b>. Se registra la decisión y se le avisa a quien la cápsula haya declarado. <b>El caso no se frena ni cambia de estado</b>: qué hacer con un rechazo lo decidís vos.</>}
        ejemplo={<>Pedís la autorización del gerente del área de quien lo pidió. Le llega un aviso, entra a <i>Mis solicitudes</i>, aprueba o rechaza con un comentario, y vos lo ves acá.</>}
        ojo={<>Si alguien <b>rechaza</b>, las autorizaciones que quedaban esperando se <b>cancelan</b>: nadie firma algo que ya no va a pasar. El caso sigue abierto y podés volver a pedir cuando lo replantees.</>}
      />

      {abriendo && (
        <div className="mt-2 space-y-2 bg-zinc-50 border border-brand-border rounded-sm p-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500">Quién autoriza</label>
            <select
              className="w-full h-8 text-xs rounded-sm border border-brand-border bg-white px-2 mt-0.5"
              value={como} data-testid="como-se-resuelve"
              onChange={(e) => { setComo(e.target.value); setValor(''); }}
            >
              {COMO.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            {/* **Se guarda la forma de resolverlo, no la persona.** Una plantilla que nombrara a
                alguien queda desactualizada el día que esa persona cambie de puesto. */}
            {/* **La pista dice de dónde sale**, y por eso está acá y no en la ayuda: es lo que
                hace falta saber en el momento de elegir, no después. */}
            <p className="text-[10px] text-zinc-500 mt-0.5">
              {necesitaValor
                ? 'Se guarda a quién elegiste.'
                : (COMO.find((c) => c.id === como)?.pista
                   || 'Se resuelve al crearla, mirando el organigrama y los roles del área.')}
            </p>
          </div>

          {como === 'rol' && (
            <select className="w-full h-8 text-xs rounded-sm border border-brand-border bg-white px-2"
                    value={valor} onChange={(e) => setValor(e.target.value)}>
              <option value="">— elegí un rol —</option>
              {teamRoles.map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
            </select>
          )}
          {/* **Las dos usan el mismo selector y sólo cambian de dónde sacan la gente.** Antes
              eran dos controles distintos —un desplegable plano para el equipo y un buscador
              hecho a mano para el directorio— que se veían y se usaban distinto para la misma
              pregunta. */}
          {como === 'persona' && (
            <SelectorDePersona
              testId="autorizante-del-equipo"
              value={valor} onChange={(contactId) => setValor(contactId)}
              opciones={personas}
              sinElegir="— elegí una persona del equipo —"
              placeholder="Escribí un nombre del equipo…"
            />
          )}
          {como === 'persona_directorio' && (
            <SelectorDePersona
              testId="autorizante-del-directorio"
              value={valor} onChange={(contactId) => setValor(contactId)}
              sinElegir="— elegí a cualquiera de la organización —"
              placeholder="Escribí un nombre o apellido…"
            />
          )}

          <Input className="h-8 text-xs rounded-sm" placeholder="Título (opcional): «Autorizar reposición de celular»"
                 value={titulo} onChange={(e) => setTitulo(e.target.value)} />

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm"
                    onClick={() => setAbriendo(false)}>Cancelar</Button>
            <Button size="sm" className="h-7 text-xs rounded-sm" disabled={guardando} onClick={pedir}>
              {guardando && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Pedir
            </Button>
          </div>
        </div>
      )}

      {!autorizaciones.length ? (
        <p className="text-[11px] text-zinc-500 mt-1">Este caso no tiene autorizaciones pedidas.</p>
      ) : (
        <div className="mt-2 space-y-1">
          {autorizaciones.map((a) => {
            const d = a.autorizacion || {};
            const e = ESTADO[d.resultado];
            const Icono = e?.icono || Clock;
            const quien = d.resuelta_por || d.autorizante_previsto?.nombre
              || d.autorizante_previsto?.email;
            return (
              <div key={a.task_id}
                   className="flex items-start gap-2 py-1 border-b border-brand-border last:border-0">
                <Icono className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${e?.clase || 'text-amber-600'}`} />
                <div className="min-w-0">
                  <p className="text-xs">
                    <span className={e?.clase || 'text-amber-700'}>{e?.texto || 'Esperando'}</span>
                    {quien ? <span className="text-zinc-600"> · {quien}</span>
                           : <span className="text-amber-700"> · sin autorizante resuelto</span>}
                  </p>
                  <p className="text-[10px] text-zinc-500">{a.title}</p>
                  {/* **Por qué no se pudo y dónde se arregla.** Antes decía sólo «sin autorizante
                      resuelto», que informa del síntoma y deja a quien lo lee sin saber si se
                      equivocó, si falta cargar algo o si está roto. */}
                  {!quien && d.sin_autorizante_porque && (
                    <p className="text-[10px] text-amber-800 mt-0.5">{d.sin_autorizante_porque}</p>
                  )}
                  {d.motivo && (
                    <p className="text-[11px] text-zinc-600 mt-0.5 whitespace-pre-wrap">{d.motivo}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
